import React, { useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, SvgIcon, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import { useLayoutMappingMetrics } from "../components/LayoutMapping";
import { SECTOR_OPTIONS } from "../config/businessTaxonomy";
import { ARAGON_COLORS, ARAGON_FONTS, AragonFontLoader } from "../theme/AragonTheme";

function InformationIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M6.7 3.5h7.7l3.9 3.9v13.1H6.7V3.5Zm1.8 1.8v13.4h8V8.2h-3V5.3h-5Zm2 5h4.3v1.6h-4.3v-1.6Zm0 3.3h4.9v1.6h-4.9v-1.6Z" />
    </SvgIcon>
  );
}
function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function useBuyerInformationMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      pagePadX: Math.round(d3.scaleLinear().domain([320, 1920]).range([18, 64]).clamp(true)(width)),
      pagePadY: Math.round(d3.scaleLinear().domain([600, 1080]).range([22, 36]).clamp(true)(height)),
      contentMax: Math.round(d3.scaleLinear().domain([760, 1920]).range([690, 980]).clamp(true)(width)),
      formMax: Math.round(d3.scaleLinear().domain([760, 1920]).range([660, 900]).clamp(true)(width)),
      headingSize: Math.round(d3.scaleLinear().domain([320, 1920]).range([31, 42]).clamp(true)(width)),
      introGap: Math.round(d3.scaleLinear().domain([600, 1080]).range([20, 30]).clamp(true)(height)),
      fieldGap: Math.round(d3.scaleLinear().domain([320, 1920]).range([14, 20]).clamp(true)(width)),
      budgetColumns: width >= 720 ? "1fr 1fr" : "1fr",
      sidebarPad: Math.round(d3.scaleLinear().domain([768, 1920]).range([10, 14]).clamp(true)(width)),
      sidebarLogo: Math.round(d3.scaleLinear().domain([768, 1920]).range([28, 36]).clamp(true)(width)),
      sidebarIconBox: Math.round(d3.scaleLinear().domain([768, 1920]).range([30, 34]).clamp(true)(width)),
      sidebarIcon: Math.round(d3.scaleLinear().domain([768, 1920]).range([16, 18]).clamp(true)(width)),
    };
  }, [shared]);
}
function BuyerInformationSidebar({ metrics }) {
  const [expanded, setExpanded] = useState(false);
  const canHover = !metrics.compact;
  const width = expanded && canHover ? metrics.sidebarExpanded : metrics.sidebarCollapsed;
  return (
    <Box
      component="aside"
      onMouseEnter={() => canHover && setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      sx={{
        position: "fixed",
        inset: "0 auto 0 0",
        width: `${width}px`,
        bgcolor: ARAGON_COLORS.navy,
        color: ARAGON_COLORS.sellerSidebar,
        zIndex: 20,
        overflow: "hidden",
        transition: "width 240ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 240ms ease",
        boxShadow: expanded && canHover ? `14px 0 34px ${alpha(ARAGON_COLORS.navy, 0.18)}` : "none",
        borderRight: `1px solid ${alpha(ARAGON_COLORS.sellerSidebar, 0.08)}`,
      }}
      aria-label="Buyer onboarding status: Information"
    >
      <Box sx={{ height: "100%", px: `${metrics.sidebarPad}px`, py: `${metrics.sidebarPad + 2}px` }}>
        <Box sx={{ display: "flex", alignItems: "center", minHeight: 42, whiteSpace: "nowrap" }}>
          <Box component="img" src="/images/1080 x 1080 px Logo 1.png" alt="Aragon" sx={{ width: metrics.sidebarLogo, height: metrics.sidebarLogo, objectFit: "contain", flex: "0 0 auto" }} />
          <Box sx={{ ml: 1.4, opacity: expanded ? 1 : 0, transform: expanded ? "translateX(0)" : "translateX(-6px)", transition: "opacity 180ms ease, transform 180ms ease", pointerEvents: "none" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: 19, lineHeight: 1 }}>Aragon</Typography>
            <Typography sx={{ mt: 0.55, fontFamily: ARAGON_FONTS.secondary, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: alpha(ARAGON_COLORS.sellerSidebar, 0.54) }}>
              Buyer profile
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: `${metrics.sidebarNavTop + 4}px`, display: "flex", alignItems: "flex-start", minHeight: 60, whiteSpace: "nowrap" }}>
          <Box sx={{ width: metrics.sidebarIconBox, height: metrics.sidebarIconBox, borderRadius: "50%", display: "grid", placeItems: "center", border: `1px solid ${alpha(ARAGON_COLORS.ivory, 0.85)}`, bgcolor: alpha(ARAGON_COLORS.ivory, 0.12), flex: "0 0 auto" }}>
            <InformationIcon sx={{ fontSize: metrics.sidebarIcon, color: ARAGON_COLORS.ivory }} />
          </Box>
          <Box sx={{ ml: 1.6, pt: 0.15, opacity: expanded ? 1 : 0, transform: expanded ? "translateX(0)" : "translateX(-5px)", transition: "opacity 180ms ease, transform 180ms ease", pointerEvents: "none" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: 13, fontWeight: 700, color: ARAGON_COLORS.sellerSidebar }}>Information</Typography>
            <Typography sx={{ mt: 0.3, fontFamily: ARAGON_FONTS.secondary, fontSize: 10.5, color: alpha(ARAGON_COLORS.sellerSidebar, 0.5) }}>Acquisition preferences</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
function sanitizeBudgetInput(value) {
  return String(value || "").replace(/[^0-9]/g, "");
}
function BuyerInformation() {
  const metrics = useBuyerInformationMetrics();
  const navigate = useNavigate();
  const [form, setForm] = useState({ desiredSector: "", desiredSubsector: "", budgetMin: "", budgetMax: "" });
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState("");
  const fieldSx = {
    alignSelf: "start",
    "& .MuiOutlinedInput-root": {
      bgcolor: alpha(ARAGON_COLORS.sellerSidebar, 0.72),
      fontFamily: ARAGON_FONTS.secondary,
      borderRadius: `${Math.max(8, metrics.cardRadius)}px`,
      "& fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.16) },
      "&:hover fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.30) },
      "&.Mui-focused fieldset": { borderColor: ARAGON_COLORS.navy },
    },
    "& .MuiInputLabel-root, & .MuiFormHelperText-root": { fontFamily: ARAGON_FONTS.secondary },
    "& .MuiFormHelperText-root": { mt: 0.55, mx: 0.15, lineHeight: 1.35, color: ARAGON_COLORS.steel },
  };
  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    setPageError("");
  };
  const validate = () => {
    const nextErrors = {};
    if (!form.desiredSector) nextErrors.desiredSector = "Desired sector is required.";
    if (!form.desiredSubsector.trim()) nextErrors.desiredSubsector = "Desired subsector is required.";
    const min = Number(form.budgetMin);
    const max = Number(form.budgetMax);
    if (!form.budgetMin || !Number.isFinite(min) || min <= 0) nextErrors.budgetMin = "Enter a minimum budget greater than zero.";
    if (!form.budgetMax || !Number.isFinite(max) || max <= 0) nextErrors.budgetMax = "Enter a maximum budget greater than zero.";
    if (!nextErrors.budgetMin && !nextErrors.budgetMax && max < min) nextErrors.budgetMax = "Maximum budget must be greater than or equal to minimum budget.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
  const submit = (event) => {
    event.preventDefault();
    if (!validate()) {
      setPageError("Complete the acquisition criteria before continuing.");
      return;
    }
    const snapshot = {
      desiredSector: form.desiredSector,
      desiredSubsector: form.desiredSubsector.trim(),
      budgetMin: Number(form.budgetMin),
      budgetMax: Number(form.budgetMax),
    };
    navigate("/buyer/dashboard", { replace: true, state: { buyerInformation: snapshot } });
  };
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: ARAGON_COLORS.sellerCanvas, color: ARAGON_COLORS.navy }}>
      <AragonFontLoader />
      <BuyerInformationSidebar metrics={metrics} />
      <Box sx={{ ml: `${metrics.sidebarCollapsed}px`, minHeight: "100dvh", minWidth: 0 }}>
        <Box component="main" sx={{ minHeight: "100dvh", boxSizing: "border-box", px: `${metrics.pagePadX}px`, py: `${metrics.pagePadY}px` }}>
          <Box sx={{ width: "100%", maxWidth: `${metrics.contentMax}px`, mx: "auto" }}>
            <Box sx={{ maxWidth: `${metrics.formMax}px` }}>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
                Buyer information
              </Typography>
              <Typography component="h1" sx={{ mt: "7px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.headingSize}px`, lineHeight: 1.03, letterSpacing: "-0.025em", color: ARAGON_COLORS.navy }}>
                Acquisition criteria
              </Typography>
              <Typography sx={{ mt: "8px", maxWidth: 680, fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, lineHeight: 1.55, color: ARAGON_COLORS.steel }}>
                Tell Aragon what kind of business you want to acquire. Sector and budget define eligibility; subsector improves relevance inside the eligible market.
              </Typography>
            </Box>
            <Box component="form" onSubmit={submit} noValidate sx={{ mt: `${metrics.introGap}px`, maxWidth: `${metrics.formMax}px`, display: "flex", flexDirection: "column", gap: `${metrics.fieldGap}px` }}>
              <TextField
                select
                label="Desired Sector"
                value={form.desiredSector}
                onChange={(event) => update("desiredSector", event.target.value)}
                error={Boolean(errors.desiredSector)}
                helperText={errors.desiredSector || "Uses the same canonical sector taxonomy as Seller Information."}
                fullWidth
                required
                sx={fieldSx}
              >
                {SECTOR_OPTIONS.map((sector) => <MenuItem key={sector} value={sector} sx={{ fontFamily: ARAGON_FONTS.secondary }}>{sector}</MenuItem>)}
              </TextField>
              <TextField
                label="Desired Subsector"
                value={form.desiredSubsector}
                onChange={(event) => update("desiredSubsector", event.target.value)}
                error={Boolean(errors.desiredSubsector)}
                helperText={errors.desiredSubsector || "A soft preference used to improve matching relevance within the selected sector."}
                fullWidth
                required
                sx={fieldSx}
              />
              <Box sx={{ pt: `${Math.max(2, Math.round(metrics.fieldGap * 0.15))}px` }}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, lineHeight: 1.1, color: ARAGON_COLORS.navy }}>
                  Acquisition budget
                </Typography>
                <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, lineHeight: 1.5, color: ARAGON_COLORS.steel }}>
                  Define the minimum and maximum transaction value you are prepared to consider.
                </Typography>
                <Box sx={{ mt: `${Math.max(12, metrics.fieldGap - 2)}px`, display: "grid", gridTemplateColumns: metrics.budgetColumns, gap: `${metrics.fieldGap}px` }}>
                  <TextField
                    label="Minimum"
                    value={form.budgetMin}
                    onChange={(event) => update("budgetMin", sanitizeBudgetInput(event.target.value))}
                    error={Boolean(errors.budgetMin)}
                    helperText={errors.budgetMin || "Minimum acquisition budget"}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    fullWidth
                    required
                    sx={fieldSx}
                  />
                  <TextField
                    label="Maximum"
                    value={form.budgetMax}
                    onChange={(event) => update("budgetMax", sanitizeBudgetInput(event.target.value))}
                    error={Boolean(errors.budgetMax)}
                    helperText={errors.budgetMax || "Maximum acquisition budget"}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    fullWidth
                    required
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              {pageError && <Alert severity="error" sx={{ fontFamily: ARAGON_FONTS.secondary }}>{pageError}</Alert>}
              <Box sx={{ pt: `${Math.max(4, metrics.fieldGap * 0.35)}px`, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  endIcon={<ArrowIcon sx={{ fontSize: "15px !important" }} />}
                  sx={{ minHeight: 38, px: "20px", bgcolor: ARAGON_COLORS.navy, color: ARAGON_COLORS.sellerSidebar, borderRadius: `${Math.max(7, metrics.cardRadius - 1)}px`, fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, "&:hover": { bgcolor: ARAGON_COLORS.burgundy } }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default BuyerInformation;
